import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DatabaseService } from './database.service';
import type {
  OrgTreeNode,
  OrgTreeEmployeeNode,
  OrgTreeSegmentNode,
} from '../modules/asignaciones/utils/asignaciones-org-tree.util';
import type { WeeklyRecapData } from '../modules/asignaciones/utils/ticket-weekly-recap.util';

export type TicketType = string;
export const TICKET_STATUSES = [
  'pendiente',
  'en_proceso',
  'esperando_revision',
  'en_revision',
  'esperando_retrabajo',
  'retrabajo',
  'terminado',
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
export type TicketPriority = 'bajo' | 'medio' | 'alto' | 'critico';

export interface TicketTypeConfig {
  id: number;
  workareaId: number;
  code: string;
  label: string;
  icon: string;
  color: string;
  sortOrder: number;
  active: boolean;
  workarea?: Workarea;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTicketTypeDto {
  workareaId: number;
  code: string;
  label: string;
  icon?: string;
  color?: string;
  sortOrder?: number;
  active?: boolean;
}

export type UpdateTicketTypeDto = Partial<CreateTicketTypeDto>;

export interface EmployeeRef {
  id: number;
  name: string;
  surname: string;
  email?: string;
  photo?: string;
}

export interface Workarea {
  id: number;
  name: string;
}

export interface TicketAssignee {
  id: number;
  ticketId: number;
  employeeId: number;
  employee?: EmployeeRef;
}

export interface TicketComment {
  id: number;
  ticketId: number;
  authorId: number;
  body: string;
  parentCommentId?: number | null;
  author?: EmployeeRef;
  createdAt: string;
}

export interface CommentThread {
  comment: TicketComment;
  replies: TicketComment[];
}

export interface TicketImage {
  id: number;
  ticketId: number;
  commentId?: number | null;
  s3Key: string;
  url: string;
  originalname?: string | null;
  mimetype?: string | null;
}

export interface TicketLink {
  id: number;
  ticketId: number;
  relatedTicketId: number;
  relationType: string;
  relatedTicket?: Pick<Ticket, 'id' | 'title' | 'status' | 'type'>;
}

export type TicketGitlabRefKind = 'commit' | 'merge_request';

export interface TicketGitlabRef {
  id: number;
  ticketId: number;
  kind: TicketGitlabRefKind;
  gitlabProjectId: string;
  gitlabProjectPath?: string | null;
  externalId: string;
  title?: string | null;
  webUrl: string;
  authorName?: string | null;
  branchName?: string | null;
  state?: string | null;
  shortSha?: string | null;
  occurredAt?: string | null;
  createdAt?: string;
}

export type TicketActivityAction =
  | 'created'
  | 'status_changed'
  | 'title_updated'
  | 'description_updated'
  | 'type_changed'
  | 'workarea_changed'
  | 'assignees_changed'
  | 'comment_added'
  | 'link_added'
  | 'link_removed'
  | 'image_added'
  | 'priority_changed'
  | 'story_points_changed'
  | 'mentioned';

export interface TicketActivity {
  id: number;
  ticketId: number;
  actorId: number;
  action: TicketActivityAction;
  oldValue?: string | null;
  newValue?: string | null;
  metadata?: Record<string, unknown> | null;
  actor?: EmployeeRef;
  createdAt: string;
}

export interface EmployeeNotification {
  id: number;
  employeeId: number;
  ticketId: number;
  activityId?: number | null;
  message: string;
  readAt?: string | null;
  ticket?: Pick<Ticket, 'id' | 'title' | 'status' | 'type'>;
  createdAt: string;
}

export interface PaginatedNotifications {
  notifications: EmployeeNotification[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

export interface Ticket {
  id: number;
  title: string;
  description?: string;
  /** Resumen de texto plano en listados (sin HTML ni imágenes embebidas). */
  descriptionPreview?: string;
  type: TicketType;
  status: TicketStatus;
  reporterId: number;
  reporter?: EmployeeRef;
  workareaId?: number;
  workarea?: Workarea;
  order: number;
  priority: TicketPriority;
  storyPoints?: number | null;
  completedAt?: string | null;
  assignees?: TicketAssignee[];
  comments?: TicketComment[];
  images?: TicketImage[];
  links?: TicketLink[];
  activities?: TicketActivity[];
  gitlabRefs?: TicketGitlabRef[];
  createdAt: string;
  updatedAt: string;
}

export interface TicketImagePayload {
  fileBuffer: string; // base64 (sin prefijo data:)
  originalname?: string;
  mimetype?: string;
  pendingId?: string;
}

export interface CreateTicketDto {
  title: string;
  description?: string;
  type: TicketType;
  status?: TicketStatus;
  reporterId: number;
  workareaId?: number;
  priority?: TicketPriority;
  storyPoints?: number | null;
  assigneeIds?: number[];
  relatedTicketIds?: number[];
  images?: TicketImagePayload[];
  actorEmployeeId?: number;
}

export type UpdateTicketDto = Partial<CreateTicketDto>;

export type { OrgTreeNode, OrgTreeEmployeeNode, OrgTreeSegmentNode };

export {
  isOrgTreeEmployeeNode,
  isOrgTreeSegmentNode,
} from '../modules/asignaciones/utils/asignaciones-org-tree.util';

export interface EmployeeProfileSchedule {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

export interface EmployeeProfileCard {
  id: number;
  name: string;
  surname: string;
  email?: string;
  phone?: string;
  celphone?: string;
  photo?: string;
  workarea?: Workarea | null;
  supervisor?: Pick<EmployeeRef, 'id' | 'name' | 'surname'> | null;
  directReportsCount: number;
  directReports: Pick<EmployeeRef, 'id' | 'name' | 'surname'>[];
  schedules: EmployeeProfileSchedule[];
}

export interface ApiResponse<T> {
  ok: boolean;
  status: number;
  data?: T;
  err?: string;
  message?: string;
}

export interface TicketListParams {
  page?: number;
  limit?: number;
  workareaId?: number;
  assigneeId?: number;
  reporterId?: number;
  status?: TicketStatus;
  type?: TicketType;
}

export interface PaginatedTickets {
  tickets: Ticket[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

export interface WeeklyRecapParams {
  startDate: string;
  endDate: string;
  workareaId?: number;
}

/**
 * Servicio del módulo Asignaciones (mini-Jira).
 *
 * Consume el gateway REST (environment.baseUrl). El gateway debe reenviar
 * estas rutas a los @MessagePattern del microservicio sc-ms-mb-employees:
 *   GET    /tickets                 -> tickets.get_all
 *   GET    /tickets/weekly-recap    -> tickets.weekly_recap
 *   GET    /tickets/:id             -> tickets.get_by_id
 *   POST   /tickets                 -> tickets.create
 *   PUT    /tickets/:id             -> tickets.update
 *   PATCH  /tickets/:id/status      -> tickets.update_status
 *   DELETE /tickets/:id             -> tickets.delete
 *   POST   /tickets/:id/comments    -> tickets.add_comment
 *   POST   /tickets/:id/images      -> tickets.add_image
 *   POST   /tickets/:id/links       -> tickets.add_link
 *   DELETE /tickets/:id/links/:rid  -> tickets.remove_link
 *   GET    /tickets/org-tree        -> tickets.get_org_tree
 *   PATCH  /tickets/supervisor      -> tickets.set_supervisor
 * Áreas y empleados reutilizan rutas existentes del gateway.
 */
@Injectable({ providedIn: 'root' })
export class AsignacionesService {
  private readonly employeeProfileCache = new Map<number, Observable<EmployeeProfileCard | null>>();

  constructor(private db: DatabaseService) {}

  getTickets(params: TicketListParams = {}): Observable<ApiResponse<PaginatedTickets>> {
    const clean: any = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') clean[k] = v;
    });
    return this.db.getWithParams<ApiResponse<PaginatedTickets>>('/tickets', clean);
  }

  getWeeklyRecap(params: WeeklyRecapParams): Observable<ApiResponse<WeeklyRecapData>> {
    const clean: Record<string, string | number> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') clean[k] = v as string | number;
    });
    return this.db.getWithParams<ApiResponse<WeeklyRecapData>>('/tickets/weekly-recap', clean);
  }

  getTicket(id: number): Observable<ApiResponse<Ticket>> {
    return this.db.get<ApiResponse<Ticket>>(`/tickets/${id}`);
  }

  createTicket(dto: CreateTicketDto): Observable<ApiResponse<Ticket>> {
    return this.db.post<ApiResponse<Ticket>>('/tickets', dto);
  }

  updateTicket(id: number, dto: UpdateTicketDto): Observable<ApiResponse<Ticket>> {
    return this.db.put<ApiResponse<Ticket>>(`/tickets/${id}`, { updateTicketDto: dto });
  }

  updateStatus(id: number, status: TicketStatus, order?: number, actorEmployeeId?: number): Observable<ApiResponse<Ticket>> {
    return this.db.post<ApiResponse<Ticket>>(`/tickets/${id}/status`, { status, order, actorEmployeeId });
  }

  deleteTicket(id: number): Observable<ApiResponse<void>> {
    return this.db.delete<ApiResponse<void>>(`/tickets/${id}`);
  }

  addComment(
    ticketId: number,
    authorId: number,
    body: string,
    parentCommentId?: number | null,
    images?: TicketImagePayload[],
  ): Observable<ApiResponse<TicketComment>> {
    return this.db.post<ApiResponse<TicketComment>>(`/tickets/${ticketId}/comments`, {
      ticketId,
      authorId,
      body,
      ...(parentCommentId != null ? { parentCommentId } : {}),
      ...(images?.length ? { images } : {}),
    });
  }

  addImage(
    ticketId: number,
    image: TicketImagePayload,
    actorEmployeeId?: number,
    commentId?: number | null,
  ): Observable<ApiResponse<TicketImage>> {
    return this.db.post<ApiResponse<TicketImage>>(`/tickets/${ticketId}/images`, {
      ticketId,
      actorEmployeeId,
      ...(commentId != null ? { commentId } : {}),
      ...image,
    });
  }

  addLink(ticketId: number, relatedTicketId: number, actorEmployeeId?: number): Observable<ApiResponse<void>> {
    return this.db.post<ApiResponse<void>>(`/tickets/${ticketId}/links`, {
      ticketId,
      relatedTicketId,
      actorEmployeeId,
    });
  }

  removeLink(ticketId: number, relatedTicketId: number, actorEmployeeId?: number): Observable<ApiResponse<void>> {
    const qs = actorEmployeeId != null ? `?actorEmployeeId=${actorEmployeeId}` : '';
    return this.db.delete<ApiResponse<void>>(`/tickets/${ticketId}/links/${relatedTicketId}${qs}`);
  }

  getOrgTree(rootId?: number): Observable<ApiResponse<OrgTreeNode[]>> {
    return this.db.getWithParams<ApiResponse<OrgTreeNode[]>>(
      '/tickets/org-tree',
      rootId ? { rootId } : {},
    );
  }

  setSupervisor(employeeId: number, supervisorId: number | null): Observable<ApiResponse<any>> {
    return this.db.put<ApiResponse<any>>('/tickets/supervisor', { employeeId, supervisorId });
  }

  setOrgMember(payload: {
    employeeId: number;
    supervisorId?: number | null;
    orgSegmentLabel?: string | null;
    orgTreeRoot?: boolean | null;
  }): Observable<ApiResponse<any>> {
    return this.db.put<ApiResponse<any>>('/tickets/org-member', payload);
  }

  removeOrgNode(payload: {
    type: 'employee' | 'segment';
    employeeId?: number;
    parentEmployeeId?: number;
    label?: string;
  }): Observable<ApiResponse<{ removedCount: number }>> {
    return this.db.post<ApiResponse<{ removedCount: number }>>('/tickets/org-node/remove', payload);
  }

  getEmployeeProfile(employeeId: number): Observable<EmployeeProfileCard | null> {
    if (!this.employeeProfileCache.has(employeeId)) {
      const request$ = this.db.get<ApiResponse<EmployeeProfileCard>>(`/tickets/employees/${employeeId}/profile`).pipe(
        map((res) => (res?.ok ? res.data ?? null : null)),
        shareReplay(1),
      );
      this.employeeProfileCache.set(employeeId, request$);
    }
    return this.employeeProfileCache.get(employeeId)!;
  }

  /** Áreas de trabajo (reutiliza ruta existente del gateway). */
  getWorkareas(): Observable<ApiResponse<Workarea[]>> {
    return this.db.get<ApiResponse<Workarea[]>>('/employees/workareas');
  }

  /** Empleados activos (reutiliza ruta existente del gateway). */
  getActiveEmployees(): Observable<ApiResponse<EmployeeRef[]>> {
    return this.db.get<ApiResponse<EmployeeRef[]>>('/employees/all/active');
  }

  /** Empleado de la sesión SSO (email/username en RRHH). */
  getCurrentEmployee(): Observable<ApiResponse<EmployeeRef>> {
    return this.db.get<ApiResponse<EmployeeRef>>('/employees/me');
  }

  getNotifications(employeeId: number, limit = 30, page = 1): Observable<ApiResponse<PaginatedNotifications>> {
    return this.db.getWithParams<ApiResponse<PaginatedNotifications>>('/tickets/notifications', {
      employeeId,
      limit,
      page,
    });
  }

  getNotificationUnreadCount(employeeId: number): Observable<ApiResponse<{ count: number }>> {
    return this.db.getWithParams<ApiResponse<{ count: number }>>('/tickets/notifications/unread-count', {
      employeeId,
    });
  }

  markNotificationRead(id: number, employeeId: number): Observable<ApiResponse<EmployeeNotification>> {
    return this.db.put<ApiResponse<EmployeeNotification>>(`/tickets/notifications/${id}/read`, { employeeId });
  }

  markAllNotificationsRead(employeeId: number): Observable<ApiResponse<void>> {
    return this.db.put<ApiResponse<void>>('/tickets/notifications/read-all', { employeeId });
  }

  getTicketTypes(workareaId?: number, includeInactive = false): Observable<ApiResponse<TicketTypeConfig[]>> {
    const params: Record<string, string | number> = {};
    if (workareaId != null) params['workareaId'] = workareaId;
    if (includeInactive) params['includeInactive'] = 'true';
    return this.db.getWithParams<ApiResponse<TicketTypeConfig[]>>('/tickets/types', params);
  }

  getAllTicketTypes(includeInactive = false): Observable<ApiResponse<TicketTypeConfig[]>> {
    return this.getTicketTypes(undefined, includeInactive);
  }

  createTicketType(dto: CreateTicketTypeDto): Observable<ApiResponse<TicketTypeConfig>> {
    return this.db.post<ApiResponse<TicketTypeConfig>>('/tickets/types', dto);
  }

  updateTicketType(id: number, dto: UpdateTicketTypeDto): Observable<ApiResponse<TicketTypeConfig>> {
    return this.db.put<ApiResponse<TicketTypeConfig>>(`/tickets/types/${id}`, { updateTicketTypeDto: dto });
  }

  deleteTicketType(id: number): Observable<ApiResponse<void>> {
    return this.db.delete<ApiResponse<void>>(`/tickets/types/${id}`);
  }
}

/** Convierte un File a base64 (sin el prefijo data:...;base64,). */
export function fileToBase64(file: File): Promise<TicketImagePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve({ fileBuffer: base64, originalname: file.name, mimetype: file.type });
    };
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

/** Valores legacy (SISTEMAS / Jira clásico) — fallback si no hay catálogo cargado. */
export const LEGACY_TICKET_TYPES: TicketType[] = ['pregunta', 'tarea', 'error', 'historia'];

export const LEGACY_TICKET_TYPE_LABELS: Record<string, string> = {
  pregunta: 'Pregunta',
  tarea: 'Tarea',
  error: 'Error',
  historia: 'Historia',
};

export const LEGACY_TICKET_TYPE_ICONS: Record<string, string> = {
  pregunta: 'mat:help_outline',
  tarea: 'mat:task_alt',
  error: 'mat:bug_report',
  historia: 'mat:menu_book',
};

export const LEGACY_TICKET_TYPE_COLORS: Record<string, string> = {
  pregunta: '#6554c0',
  tarea: '#0079bf',
  error: '#eb5a46',
  historia: '#61bd4f',
};

/** @deprecated Usar TicketTypeCatalogService */
export const TICKET_TYPES = LEGACY_TICKET_TYPES;

/** @deprecated Usar TicketTypeCatalogService */
export const TICKET_TYPE_LABELS = LEGACY_TICKET_TYPE_LABELS;

/** @deprecated Usar TicketTypeCatalogService */
export const TICKET_TYPE_ICONS = LEGACY_TICKET_TYPE_ICONS;

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  esperando_revision: 'Esperando Revisión',
  en_revision: 'En Revisión',
  esperando_retrabajo: 'Esperando Re-Trabajo',
  retrabajo: 'Re-trabajo',
  terminado: 'Terminado',
};

export const TICKET_PRIORITIES: TicketPriority[] = ['bajo', 'medio', 'alto', 'critico'];

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  bajo: 'Bajo',
  medio: 'Medio',
  alto: 'Alto',
  critico: 'Crítico',
};

export const TICKET_PRIORITY_ICONS: Record<TicketPriority, string> = {
  bajo: 'mat:arrow_downward',
  medio: 'mat:drag_handle',
  alto: 'mat:arrow_upward',
  critico: 'mat:priority_high',
};

/** Días máximos que se muestran tickets terminados en tablero y lista. */
export const TICKET_TERMINADO_BOARD_MAX_DAYS = 14;

/** Resuelve la URL de visualización de una imagen de ticket vía gateway. */
export function getTicketImageUrl(image: Pick<TicketImage, 'id' | 's3Key' | 'url'>): string {
  if (image.id) {
    return `${environment.baseUrl}/tickets/images/${image.id}/file`;
  }
  if (image.s3Key) {
    return `${environment.baseUrl}/s3/get-file?uuid=${encodeURIComponent(image.s3Key)}`;
  }
  return image.url || '';
}
